import React from "react";
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Loader2, Save, X, WandSparkles, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

interface NewPageFooterProps {
  // Step navigation props
  currentStep?: number;
  totalSteps?: number;
  canGoNext?: boolean;
  canGoPrevious?: boolean;
  canSubmit?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  
  // Legacy props (for backward compatibility)
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  isValid?: boolean;
  titleCharacterCount?: number;
  purposeCharacterCount?: number;
  hasType?: boolean;
}

const NewPageFooter: React.FC<NewPageFooterProps> = ({
  // Step navigation props
  currentStep = 3,
  totalSteps = 3,
  canGoNext = false,
  canGoPrevious = false,
  canSubmit = false,
  onNext,
  onPrevious,
  
  // Legacy props
  onCancel,
  onSubmit,
  isSubmitting,
  isValid = false,
  titleCharacterCount = 0,
  purposeCharacterCount = 0,
  hasType = false,
}) => {
  // Calculate progress for legacy support or step-based progress
  const progressPercentage = currentStep && totalSteps 
    ? Math.round((currentStep / totalSteps) * 100)
    : Math.min(
        ((titleCharacterCount >= 3 ? 30 : 0) + 
         (purposeCharacterCount >= 20 ? 50 : Math.min(purposeCharacterCount / 20 * 50, 50)) +
         (hasType ? 20 : 0)), 
        100
      );

  const isOnFinalStep = currentStep === totalSteps;
  const showProgressIndicator = titleCharacterCount > 0 || purposeCharacterCount > 0 || currentStep > 1;

  return (
    <div className="flex items-center justify-between gap-4">
      {/* Left side - Progress indicator */}
      <div className="flex items-center gap-3">
        {showProgressIndicator && (
          <>
            <div className={cn(
              "text-xs font-medium transition-colors duration-200",
              progressPercentage >= 100 ? "text-green-600 dark:text-green-400" : "text-purple-600 dark:text-purple-400"
            )}>
              {Math.round(progressPercentage)}%
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <motion.div
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-500",
                    progressPercentage >= 100 ? "bg-green-500" : "bg-purple-500"
                  )}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
              
              {progressPercentage >= 100 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
                >
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  Ready
                </motion.div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Right side - Action buttons */}
      <div className="flex items-center gap-3">
        {/* Cancel Button */}
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onCancel} 
          disabled={isSubmitting}
          className="border-border/50 hover:border-border"
        >
          <X size={16} className="mr-2" />
          Cancel
        </Button>
        
        {/* Step Navigation Buttons */}
        {totalSteps > 1 && (
          <>
            {/* Previous Button */}
            {canGoPrevious && onPrevious && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  console.log('[NewPageFooter] Previous button clicked');
                  onPrevious();
                }}
                disabled={isSubmitting}
                className="border-purple-200 hover:border-purple-300 text-purple-600 hover:text-purple-700"
              >
                <ChevronLeft size={16} className="mr-1" />
                Previous
              </Button>
            )}
            
            {/* Next/Submit Button */}
            {isOnFinalStep ? (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  console.log('[NewPageFooter] Create Page button clicked');
                  onSubmit();
                }}
                disabled={!canSubmit || isSubmitting}
                className={cn(
                  "bg-purple-600 hover:bg-purple-700 text-white border-purple-600",
                  "disabled:bg-muted disabled:text-muted-foreground disabled:border-muted",
                  "transition-all duration-200"
                )}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"
                    />
                    Creating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Create Document
                  </>
                )}
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  console.log('[NewPageFooter] Next button clicked');
                  onNext?.();
                }}
                disabled={!canGoNext || isSubmitting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Next
                <ChevronRight size={16} className="ml-1" />
              </Button>
            )}
          </>
        )}
        
        {/* Legacy Single Step Button (fallback) */}
        {totalSteps <= 1 && (
          <Button
            variant="default"
            size="sm"
            onClick={onSubmit}
            disabled={!isValid || isSubmitting}
            className={cn(
              "bg-purple-600 hover:bg-purple-700 text-white border-purple-600",
              "disabled:bg-muted disabled:text-muted-foreground disabled:border-muted",
              "transition-all duration-200"
            )}
          >
            {isSubmitting ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"
                />
                Creating...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Create Document
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewPageFooter;
